// Types and constants shared between web, admin, and api

export { formatTimecode, type FormatTimeOptions } from './timecode';

// --- Enums (as const objects for CJS runtime compatibility) ---

export const UserTier = {
  FREE: 'free',
  VIP: 'vip',
  ADMIN: 'admin',
} as const;
export type UserTier = (typeof UserTier)[keyof typeof UserTier];

export const WordStatus = {
  NEW: 'new',
  LEARNING: 'learning',
  MASTERED: 'mastered',
} as const;
export type WordStatus = (typeof WordStatus)[keyof typeof WordStatus];

export const SubtitleDisplayMode = {
  BILINGUAL: 'bilingual',
  ENGLISH_ONLY: 'english_only',
  CHINESE_ONLY: 'chinese_only',
} as const;
export type SubtitleDisplayMode = (typeof SubtitleDisplayMode)[keyof typeof SubtitleDisplayMode];

export const AudioStatus = {
  NONE: 'NONE',
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  READY: 'READY',
  FAILED: 'FAILED',
} as const;
export type AudioStatus = (typeof AudioStatus)[keyof typeof AudioStatus];

export interface SubtitleAudioDTO {
  ossUrl: string;
  duration: number;
}

// --- Constants ---

export const DIFFICULTIES = [
  { value: '', label: '全部' },
  { value: 'beginner', label: '初级' },
  { value: 'intermediate', label: '中级' },
  { value: 'advanced', label: '高级' },
] as const;

export const DIFFICULTY_LABEL: Record<string, string> = {
  beginner: '初级',
  intermediate: '中级',
  advanced: '高级',
};

export const CATEGORIES = [
  { value: '', label: '全部' },
  { value: 'movie', label: '电影' },
  { value: 'news', label: '新闻' },
  { value: 'talk', label: '演讲' },
  { value: 'drama', label: '剧集' },
] as const;

export const CATEGORY_LABEL: Record<string, string> = {
  movie: '电影',
  news: '新闻',
  talk: '演讲',
  drama: '剧集',
};

// --- 文件上传规则（前后端共享，避免三端硬编码漂移） ---
// 后端 OssService.validateUploadFile 和各 FileInterceptor 的 limits 都以此为准；
// 前端在选文件入口处据此提前拦截，避免把超限文件传到后端才报错。
export const UPLOAD_RULES = {
  videos: { maxBytes: 2 * 1024 * 1024 * 1024, mimePrefixes: ['video/'] },
  covers: { maxBytes: 20 * 1024 * 1024, mimePrefixes: ['image/'] },
  recordings: { maxBytes: 100 * 1024 * 1024, mimePrefixes: ['audio/', 'video/webm'] },
  audios: { maxBytes: 20 * 1024 * 1024, mimePrefixes: ['audio/'] },
} as const;
export type UploadFolder = keyof typeof UPLOAD_RULES;
// 给前端用的便捷大小常量（人类可读提示文案也可据此生成）
export const MAX_VIDEO_BYTES = UPLOAD_RULES.videos.maxBytes;
export const MAX_COVER_BYTES = UPLOAD_RULES.covers.maxBytes;
export const MAX_RECORDING_BYTES = UPLOAD_RULES.recordings.maxBytes;

// --- 客户端直传 COS（presigned URL）相关类型 ---
// 浏览器大文件不走服务器中转，改直传 COS：后端签发 presigned URL，浏览器 PUT 上去。
// 这样大文件走 COS 自己的 CDN 节点，绕开服务器窄带宽瓶颈。

/** 签名请求体：浏览器告诉后端要传什么，后端校验后签发上传凭证 */
export interface PresignUploadRequest {
  folder: UploadFolder;
  filename: string;
  contentType: string;
  size: number;
}

/** 签名响应：浏览器拿到后直接 PUT 文件到 uploadUrl，accessUrl 用于存库 */
export interface PresignUploadResponse {
  /** presigned PUT URL，浏览器用裸 fetch PUT 文件到此处 */
  uploadUrl: string;
  /** COS object key（如 videos/uuid.mp4） */
  key: string;
  /** 存库用的完整访问 URL（buildFileUrl 拼好，兼容 extractKey/getAccessibleUrl/deleteFile） */
  accessUrl: string;
  /** 签名过期时间（unix 秒），浏览器据此判断凭证是否失效 */
  expiresAt: number;
}

// --- Entity Types (API response shapes) ---

export interface User {
  id: string;
  email: string;
  nickname: string;
  avatar?: string | null;
  tier: UserTier | string;
}

export interface Video {
  id: string;
  title: string;
  description: string;
  difficulty: string;
  category: string;
  coverImage?: string | null;
  duration: number;
  published?: boolean;
  createdAt?: string;
  ossUrl?: string;
  videoUrl?: string;
  wordCount?: number;
  sentenceCount?: number;
}

export interface Subtitle {
  id: string;
  videoId?: string;
  index: number;
  english: string;
  chinese: string;
  startTime?: number | null;
  endTime?: number | null;
  approved?: boolean;
  audioStatus?: AudioStatus;
  audioError?: string | null;
}

export interface Word {
  id: string;
  videoId?: string;
  word: string;
  phonetic: string;
  partOfSpeech: string;
  definition: string;
  exampleSentenceId?: string | null;
  approved?: boolean;
}

// --- Global Word Types ---

export interface GlobalWord {
  id: string;
  word: string;
  phonetic: string;
  audioUrl: string;
  audioUs?: string;
  audioUk?: string;
  etymology: string;
  approved: boolean;
  createdAt?: string;
  updatedAt?: string;
  meanings?: Meaning[];
  videoWords?: VideoWord[];
}

export interface Meaning {
  id: string;
  globalWordId: string;
  index: number;
  partOfSpeech: string;
  definition: string;
  chineseDefinition: string;
  createdAt?: string;
  examples?: Example[];
}

export interface Example {
  id: string;
  meaningId: string;
  english: string;
  chinese: string;
  createdAt?: string;
}

export interface VideoWord {
  id: string;
  videoId: string;
  globalWordId: string;
  exampleSentenceId?: string | null;
  approved: boolean;
  globalWord?: GlobalWord;
  video?: Pick<Video, 'id' | 'title'>;
}

export interface FavoriteSentence {
  id: string;
  userId: string;
  subtitleId: string;
  createdAt: string;
  subtitle?: {
    id: string;
    videoId: string;
    english: string;
    chinese: string;
    startTime: number | null;
    endTime: number | null;
    audioStatus?: AudioStatus;
    audio?: SubtitleAudioDTO | null;
    video?: { id: string; title: string };
  };
}

export interface FavoriteWord {
  id: string;
  userId: string;
  wordId: string;
  status: WordStatus | string;
  createdAt: string;
  word?: Word & { video?: Pick<Video, 'id' | 'title'> };
  videoWordId?: string;
  videoWord?: VideoWord;
}

export interface FavoriteGlobalWord {
  id: string;
  userId: string;
  globalWordId: string;
  status: WordStatus | string;
  createdAt: string;
  globalWord?: GlobalWord;
}

export interface UserNote {
  id: string;
  userId: string;
  subtitleId: string;
  content: string;
  createdAt: string;
  updatedAt?: string;
  subtitle?: {
    id: string;
    videoId: string;
    english: string;
    chinese: string;
    video?: { id: string; title: string };
  };
}

export interface UserRecording {
  id: string;
  userId: string;
  subtitleId: string;
  recordingUrl: string;
  createdAt: string;
  subtitle?: {
    id: string;
    english: string;
    chinese: string;
    startTime: number | null;
    endTime: number | null;
    videoId: string;
  };
}

export interface RecentProgressVideo {
  id: string;
  title: string;
  coverImage?: string | null;
  duration: number;
  subtitleCount: number;
}

export interface RecentProgress {
  videoId: string;
  lastSubtitleId: string | null;
  lastSubtitleIndex: number | null;
  updatedAt: string;
  video: RecentProgressVideo;
}

export interface UpsertProgressPayload {
  videoId: string;
  lastSubtitleId?: string;
}

export interface VideoProgress {
  videoId: string;
  lastSubtitleId: string | null;
  lastSubtitleIndex: number | null;
  lastSubtitleStartTime: number | null;
  updatedAt: string;
}
